import { useState } from "react";
import { Trash2, Camera, ImageIcon, Image, MessageSquare, ChevronLeft, ChevronRight, X, ZoomIn, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
const PhotosTab = ({
  job,
  jobId,
  canManageJobs,
  uploading,
  onPhotoUpload,
  onDeletePhoto,
  onCaptionUpdate,
}) => {
  const [editCaptionOpen, setEditCaptionOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [captionText, setCaptionText] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openCaptionEdit = (photo) => {
    setEditingPhoto(photo);
    setCaptionText(photo.caption || "");
    setEditCaptionOpen(true);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextPhoto = () => {
    setLightboxIndex((prev) => (prev + 1) % sortedPhotos.length);
  };

  const prevPhoto = () => {
    setLightboxIndex((prev) => (prev - 1 + sortedPhotos.length) % sortedPhotos.length);
  };

  const handleCaptionUpdate = async () => {
    if (!editingPhoto) return;
    const success = await onCaptionUpdate(editingPhoto.id, captionText);
    if (success) {
      setEditCaptionOpen(false);
      setEditingPhoto(null);
    }
  };

  const sortedPhotos = [...(job.photos || [])].sort((a, b) => 
    (b.uploaded_at || "").localeCompare(a.uploaded_at || "")
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Job Photos</CardTitle>
          {canManageJobs && (
            <div className="flex gap-2">
              <input type="file" accept="image/*" capture="environment" onChange={onPhotoUpload} className="hidden" id="photo-camera" disabled={uploading} />
              <input type="file" accept="image/*" multiple onChange={onPhotoUpload} className="hidden" id="photo-gallery" disabled={uploading} />
              <label htmlFor="photo-camera">
                <Button size="sm" variant="outline" asChild disabled={uploading}>
                  <span className="cursor-pointer"><Camera className="w-4 h-4 mr-2" />Camera</span>
                </Button>
              </label>
              <label htmlFor="photo-gallery">
                <Button size="sm" asChild disabled={uploading}>
                  <span className="cursor-pointer"><ImageIcon className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Gallery"}</span>
                </Button>
              </label>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {sortedPhotos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedPhotos.map((photo, index) => (
                <div key={photo.id} className="relative group">
                  <div 
                    className="cursor-pointer" 
                    onClick={() => openLightbox(index)}
                    data-testid={`photo-thumbnail-${photo.id}`}
                  >
                    <img src={`/api/jobs/${jobId}/photos/${photo.id}/file?v=2&size=thumb`} alt={photo.original_name} className="w-full h-32 object-cover rounded-t-lg border border-border hover:opacity-90 transition-opacity" loading="lazy" />
                    {photo.storage_warning && (
                      <div
                        className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-medium text-white shadow"
                        title="Saved locally only — not yet backed up to OneDrive. An admin can migrate it in Settings → Backup."
                        data-testid={`photo-local-warning-${photo.id}`}
                      >
                        <AlertTriangle className="h-3 w-3" /> Local only
                      </div>
                    )}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <div className="bg-black/60 rounded-full p-1.5">
                        <ZoomIn className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-muted border border-t-0 border-border rounded-b-lg p-2 min-h-[40px]">
                    {photo.caption ? (
                      <p className="text-xs text-muted-foreground line-clamp-2">{photo.caption}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground/50 italic">No description</p>
                    )}
                    <p className="text-[10px] text-muted-foreground/70 mt-1">{photo.uploaded_at ? new Date(photo.uploaded_at).toLocaleDateString() : ""}</p>
                  </div>
                  {/* Hover overlay with buttons - only shows on hover, positioned at bottom */}
                  <div className="absolute bottom-[40px] left-0 right-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-t-lg pt-8 pb-2 pointer-events-none">
                    <div className="flex gap-1 justify-center flex-wrap pointer-events-auto">
                      <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); openCaptionEdit(photo); }}>
                        <MessageSquare className="w-3 h-3 mr-1" />Edit
                      </Button>
                      {canManageJobs && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm" onClick={(e) => e.stopPropagation()}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Photo?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently remove this photo.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDeletePhoto(photo.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No photos uploaded yet</p>
              {canManageJobs && <p className="text-sm text-muted-foreground mt-1">Use Camera or Gallery to add photos</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Photo Description Dialog */}
      <Dialog open={editCaptionOpen} onOpenChange={setEditCaptionOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Photo Description</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editingPhoto && (
              <img src={`/api/jobs/${jobId}/photos/${editingPhoto.id}/file?v=2&size=thumb`} alt={editingPhoto.original_name} className="w-full h-32 object-cover rounded-lg" />
            )}
            <div className="form-group">
              <Label>Description</Label>
              <Textarea value={captionText} onChange={(e) => setCaptionText(e.target.value)} placeholder="Add a description for this photo..." className="min-h-[80px]" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditCaptionOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCaptionUpdate} className="flex-1">Save Description</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 bg-black/95 border-none" data-testid="photo-lightbox">
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          {sortedPhotos.length > 0 && sortedPhotos[lightboxIndex] && (
            <div className="relative flex flex-col items-center justify-center min-h-[60vh]">
              {/* Navigation Arrows */}
              {sortedPhotos.length > 1 && (
                <>
                  <button
                    onClick={prevPhoto}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors z-10"
                    data-testid="lightbox-prev"
                  >
                    <ChevronLeft className="w-8 h-8 text-white" />
                  </button>
                  <button
                    onClick={nextPhoto}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors z-10"
                    data-testid="lightbox-next"
                  >
                    <ChevronRight className="w-8 h-8 text-white" />
                  </button>
                </>
              )}
              
              {/* Main Image */}
              <div className="flex-1 flex items-center justify-center p-4 pt-12">
                <img
                  src={`/api/jobs/${jobId}/photos/${sortedPhotos[lightboxIndex].id}/file?v=2`}
                  alt={sortedPhotos[lightboxIndex].original_name}
                  className="max-h-[70vh] max-w-full object-contain rounded-lg"
                  data-testid="lightbox-image"
                />
              </div>
              
              {/* Photo Info */}
              <div className="w-full bg-black/80 p-4 text-white">
                <div className="max-w-2xl mx-auto">
                  {sortedPhotos[lightboxIndex].caption && (
                    <p className="text-base mb-2">{sortedPhotos[lightboxIndex].caption}</p>
                  )}
                  <div className="flex items-center justify-between text-sm text-white/70">
                    <span>Uploaded by {sortedPhotos[lightboxIndex].uploaded_by}</span>
                    <span>{sortedPhotos[lightboxIndex].uploaded_at ? new Date(sortedPhotos[lightboxIndex].uploaded_at).toLocaleDateString() : ""}</span>
                  </div>
                  {sortedPhotos.length > 1 && (
                    <p className="text-center text-white/50 text-sm mt-2">
                      {lightboxIndex + 1} of {sortedPhotos.length}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotosTab;
